/**
 * Extensions that can execute code on the machine that opens them (executables,
 * scripts, installers) or that a browser will render inline as active HTML —
 * blocked regardless of the claimed MIME type, since MIME is client-supplied
 * and unreliable. Every other extension is uploadable.
 */
const BLOCKED_EXTENSIONS = new Set([
  "exe",
  "msi",
  "bat",
  "cmd",
  "com",
  "scr",
  "ps1",
  "psm1",
  "vbs",
  "vbe",
  "js",
  "jse",
  "wsf",
  "wsh",
  "jar",
  "apk",
  "app",
  "dmg",
  "pkg",
  "deb",
  "rpm",
  "sh",
  "bin",
  "run",
  "gadget",
  "cpl",
  "msp",
  "hta",
  "lnk",
  "reg",
  "dll",
  "sys",
  "drv",
  "iso",
  "img",
  "html",
  "htm",
  "xhtml",
  "shtml",
  "mhtml",
  "svg",
]);

export const getFileExtension = (filename: string): string => {
  const parts = filename.split(".");

  return parts.length > 1 ? (parts.pop() ?? "").toLowerCase() : "";
};

export const isBlockedFileExtension = (filename: string): boolean =>
  BLOCKED_EXTENSIONS.has(getFileExtension(filename));

/** Browsers report an empty `file.type` for these extensions (no native MIME sniffing). */
export const EXTENSION_MIME_FALLBACKS: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  jfif: "image/jfif",
  heic: "image/heic",
  heif: "image/heif",
  gif: "image/gif",
  txt: "text/plain",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsm: "application/vnd.ms-excel.sheet.macroEnabled.12",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  msg: "application/vnd.ms-outlook",
  vcf: "text/vcard",
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  mp4: "video/mp4",
  mov: "video/quicktime",
  "3gp": "video/3gpp",
  "3gpp": "video/3gpp",
  webm: "video/webm",
  avi: "video/x-msvideo",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
};

/**
 * MIME types the AI pipeline can classify/analyse, either natively or via text
 * extraction in office-extraction.ts. Everything uploadable but outside this
 * set (archives, .msg, anything unrecognized) is stored and downloadable only.
 */
const ANALYZABLE_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jfif",
  "image/heic",
  "image/heif",
  "image/gif",
  "text/plain",
  "text/csv",
  "text/vcard",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
] as const;

export const isAnalyzableMimeType = (mimeType: string): boolean =>
  (ANALYZABLE_MIME_TYPES as readonly string[]).includes(mimeType);

export const ARCHIVE_MIME_TYPES = [
  "application/zip",
  "application/vnd.rar",
  "application/x-7z-compressed",
] as const;

export const isArchiveMimeType = (mimeType: string): boolean =>
  (ARCHIVE_MIME_TYPES as readonly string[]).includes(mimeType);

/** MIME types a browser can render inline safely (no active content). */
const PREVIEWABLE_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jfif",
  "image/heic",
  "image/heif",
  "image/gif",
] as const;

/** The `Content-Type` an uploaded file's bytes are actually stored under in S3. */
export const resolveStoredContentType = (mimeType: string): string =>
  (PREVIEWABLE_MIME_TYPES as readonly string[]).includes(mimeType)
    ? mimeType
    : "application/octet-stream";

const VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/3gpp",
  "video/webm",
  "video/x-msvideo",
  "video/avi",
  "video/mpeg",
  "video/mpg",
] as const;

export const isVideoMimeType = (mimeType: string): mimeType is (typeof VIDEO_MIME_TYPES)[number] =>
  (VIDEO_MIME_TYPES as readonly string[]).includes(mimeType);

const ROUTING_MIME_TYPES = new Set<string>([
  ...ANALYZABLE_MIME_TYPES,
  ...ARCHIVE_MIME_TYPES,
  ...PREVIEWABLE_MIME_TYPES,
  ...VIDEO_MIME_TYPES,
]);

export const resolveUploadMimeType = (filename: string, reportedMimeType: string): string => {
  const extensionMimeType = EXTENSION_MIME_FALLBACKS[getFileExtension(filename)];
  if (extensionMimeType) return extensionMimeType;

  const normalizedReportedMimeType = reportedMimeType.trim().toLowerCase();
  if (!normalizedReportedMimeType || ROUTING_MIME_TYPES.has(normalizedReportedMimeType)) {
    return "application/octet-stream";
  }

  return normalizedReportedMimeType;
};
